import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import AssetOptions from "../AssetUploader/OptionsStep/AssetOptions";
import Confirmation from "../Shared/Confirmation/Confirmation";
import BackupStep from "../AssetUploader/BackupStep/BackupStep";

import { useSelector, useDispatch } from "react-redux";
import { clearSelectedCreatives } from "../../actions/app/creatives";

import { insertCreatives } from "../../services/dcm/creatives";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "50%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

function shouldUploadBackup(creatives) {
  for (let creative of creatives) {
    if (creative.file && creative.file.type) {
      if (creative.file.type.includes("zip")) return true;
    }
  }

  return false;
}

function getSteps(stepOptions) {
  let nameStepLabel = stepOptions.allExisting ? "Review URL(s)" : "Generate name(s)";
  if (stepOptions.isH5) {
    return [
      "Upload creative(s)",
      "Upload backup creative(s)",
      nameStepLabel,
      "Select Options",
      "Review"
    ];
  } else {
    return [
      "Upload creative(s)",
      nameStepLabel,
      "Select Options",
      "Review"
    ];
  }
}

const creativeTypes = [
  "DISPLAY",
  "RICH_MEDIA_DISPLAY_BANNER",
  "RICH_MEDIA_DISPLAY_EXPANDING",
  "RICH_MEDIA_DISPLAY_INTERSTITIAL"
];

function getStepContent(stepIndex, props) {
  if (stepIndex === 0) {
    return (
      <div>
        <AssetUploaderManager
          isThumbnailEnabled={true}
          filetype={"image/*, .zip"}
          creativeTypes={creativeTypes}
          selectedCreatives={props.selectedCreatives}
          setSelectedCreatives={props.setSelectedCreatives}
          placements={props.placements}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  }

  if (!props.isH5) {
    switch (stepIndex) {
      case 1:
          return (
            <div>
              <AssetNaming
                isClickEnabled={true}
                isDragDisabled={true}
                isThumbnailEnabled={true}
                ref={(r) => (props.stepRef.current = r)}
              />
            </div>
          );
      case 2:
        return (
          <div>
            <AssetOptions
              isClickEnabled={false}
              isDragDisabled={false}
              isThumbnailEnabled={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 3:
        return (
          <div>
            <AssetReview
              isClickEnabled={false}
              isDragDisabled={true}
              isThumbnailEnabled={true}
              hasCampaignData={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      default:
        return " ";
    }
  } else {
    switch (stepIndex) {
      case 1:
          return (
            <div>
              <BackupStep
                isClickEnabled={true}
                isDragDisabled={true}
                isThumbnailEnabled={false}
                ref={(r) => (props.stepRef.current = r)}
              />
            </div>
          );
      case 2:
          return (
            <div>
              <AssetNaming
                isClickEnabled={true}
                isDragDisabled={true}
                isThumbnailEnabled={true}
                ref={(r) => (props.stepRef.current = r)}
              />
            </div>
          );
      case 3:
        return (
          <div>
            <AssetOptions
              isClickEnabled={false}
              isDragDisabled={false}
              isThumbnailEnabled={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 4:
        return (
          <div>
            <AssetReview
              isClickEnabled={false}
              isDragDisabled={true}
              isThumbnailEnabled={true}
              hasCampaignData={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      default:
        return " ";
    }
  }
}

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  // Tracks an actual submission. Deriving "done" from
  // activeStep === steps.length is unsafe when the step count is derived
  // from creative state: if a step disappears while the user sits on a
  // later one, activeStep can land on steps.length and report a
  // successful submission that never happened.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [completed, setCompleted] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const creativesState = useSelector((state) => state.creatives);
  let isH5 = shouldUploadBackup(Object.values(creativesState.byId));
  let creativesArr = Object.values(creativesState.byId);
  let allExisting = creativesArr.length > 0 && creativesArr.every((c) => c.dcmCreative);
  const steps = getSteps({ isH5, allExisting });

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    dispatch(clearSelectedCreatives());
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setSelectedCreatives([]);
    setActiveStep(step);
    dispatch(clearSelectedCreatives());
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const handleTraffic = async () => {
    insertCreatives(
      Object.values(creativesState.byId),
      creativesState.assignedPlacements,
      "DISPLAY"
    );
    // Set before advancing: the Confirmation screen is gated on this,
    // not on the step index.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation
              handleResetParent={handleReset}
              isMultiAdvertiser={true}
            />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Creatives
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                  >
                    Next
                  </Button>
                )}
              </div>
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: creativesState.assignedPlacements,
                isH5,
                setActiveStep
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="warning">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
